import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import LiveControlsCard from '../components/LiveControlsCard';
import CustomerDetailsModal from '../components/CustomerDetailsModal';
import {
  QrCode, UtensilsCrossed, Music, Megaphone, Star,
  Disc3, Loader2, Sparkles, Calendar,
} from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sparkline = ({ data = [], color = '#39FF14' }) => {
  // Tiny inline sparkline using SVG path. data is a 7-entry array.
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const w = 80;
  const h = 24;
  const stepX = w / (data.length - 1 || 1);
  const points = data.map((v, i) => `${(i * stepX).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
      <defs>
        <linearGradient id={`grad-${color.replace('#','')}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon points={`0,${h} ${points} ${w},${h}`} fill={`url(#grad-${color.replace('#','')})`} />
    </svg>
  );
};

const StatCard = ({ icon: Icon, label, value, sparkline, color, testid, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className="relative bg-[#0D0D0D] border border-white/8 rounded-2xl p-3 md:p-5 hover:border-white/30 hover:bg-[#101010] transition-all text-left w-full disabled:cursor-default focus:outline-none focus:ring-2 focus:ring-white/20"
    data-testid={testid}
  >
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-2 md:gap-2.5">
        <div
          className="w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}18`, boxShadow: `inset 0 0 0 1px ${color}40` }}
        >
          <Icon className="w-4 h-4 md:w-5 md:h-5" style={{ color }} strokeWidth={2} />
        </div>
        <span className="text-[9px] md:text-[10px] uppercase tracking-[0.2em] md:tracking-[0.3em] font-['JetBrains_Mono'] text-gray-500 truncate">
          {label}
        </span>
      </div>
      {sparkline && <div className="hidden md:block"><Sparkline data={sparkline} color={color} /></div>}
    </div>
    <p className="text-2xl md:text-4xl font-extrabold text-white font-['Outfit'] mt-3 md:mt-4 tracking-tight" data-testid={`${testid}-value`}>
      {value?.toLocaleString() ?? 0}
    </p>
    <p className="text-[9px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] text-gray-600 font-['JetBrains_Mono'] mt-1 hidden md:block">
      {onClick ? 'Tap to see details' : 'Last 7 days · all time'}
    </p>
  </button>
);

const ReviewsCard = ({ data, onClick }) => {
  const COLOR = '#FFD700';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className="bg-[#0D0D0D] border border-white/8 rounded-2xl p-5 hover:border-white/30 hover:bg-[#101010] transition-all text-left w-full disabled:cursor-default focus:outline-none focus:ring-2 focus:ring-white/20"
      data-testid="dashboard-reviews-card"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${COLOR}18`, boxShadow: `inset 0 0 0 1px ${COLOR}40` }}
          >
            <Star className="w-5 h-5" style={{ color: COLOR }} strokeWidth={2} />
          </div>
          <span className="text-[10px] uppercase tracking-[0.3em] font-['JetBrains_Mono'] text-gray-500">
            Reviews
          </span>
        </div>
        <Sparkline data={data?.sparkline_7d || []} color={COLOR} />
      </div>
      <p className="text-4xl font-extrabold text-white font-['Outfit'] tracking-tight">
        {(data?.total ?? 0).toLocaleString()}
      </p>
      <p className="text-[10px] uppercase tracking-[0.2em] text-gray-600 font-['JetBrains_Mono'] mt-1 mb-4">
        Total generated · last 7 days trend
      </p>
      {data?.top_pills?.length > 0 ? (
        <div className="pt-4 border-t border-white/5">
          <p className="text-[9px] uppercase tracking-[0.3em] text-gray-500 mb-2.5 font-['JetBrains_Mono']">
            Top 3 in last 30 days
          </p>
          <ol className="space-y-1.5">
            {data.top_pills.map((p, i) => (
              <li key={p.pill} className="flex items-center justify-between text-sm" data-testid={`top-pill-${i}`}>
                <span className="text-white font-medium font-['Outfit'] flex items-center gap-2">
                  <span className="text-[#FFD700] text-xs font-mono w-4">{i + 1}.</span>
                  {p.pill}
                </span>
                <span className="text-[#FFD700] font-mono text-xs font-bold">{p.count}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <p className="text-gray-600 text-xs mt-3 italic">No review pills yet</p>
      )}
    </button>
  );
};

const ClientDashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Which tile's drill-down modal is open. null = closed.
  // Allowed values: 'scans' | 'menu_views' | 'requests' | 'shoutouts' | 'reviews' | 'events'
  const [detailsSource, setDetailsSource] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/analytics/dashboard`);
        if (mounted) setStats(r.data);
      } catch (err) {
        console.error('[Dashboard] load failed:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 30000); // refresh every 30s
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const openDetails = useCallback((source) => () => setDetailsSource(source), []);

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 pb-32 overflow-y-auto content-scroll" data-testid="client-dashboard">
        <div className="mb-6 md:mb-8">
          <p className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-gray-500 font-['JetBrains_Mono'] mb-2">
            Welcome back
          </p>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold text-white font-['Outfit'] tracking-tight">
            {user?.name || 'Venue'}
          </h1>
          <p className="text-gray-500 mt-2 text-sm md:text-base">Here&apos;s what your guests are doing right now.</p>
        </div>

        {loading && !stats ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-3 md:gap-4 lg:gap-5">
            {/* Stats row */}
            <div className="col-span-12 lg:col-span-8 grid grid-cols-2 gap-3 md:gap-4 lg:gap-5">
              <StatCard icon={QrCode}         label="QR Scans"        color="#FF007F" value={stats?.scans?.total}      sparkline={stats?.scans?.sparkline_7d}      testid="dashboard-scans-card"     onClick={openDetails('scans')} />
              <StatCard icon={UtensilsCrossed} label="Menu Views"      color="#00FFFF" value={stats?.menu_views?.total} sparkline={stats?.menu_views?.sparkline_7d} testid="dashboard-menu-card"      onClick={openDetails('menu_views')} />
              <StatCard icon={Music}          label="Song Requests"    color="#39FF14" value={stats?.requests?.total}   sparkline={stats?.requests?.sparkline_7d}   testid="dashboard-requests-card"  onClick={openDetails('requests')} />
              <StatCard icon={Megaphone}      label="Shoutouts"        color="#FF8C00" value={stats?.shoutouts?.total}  sparkline={stats?.shoutouts?.sparkline_7d}  testid="dashboard-shoutouts-card" onClick={openDetails('shoutouts')} />
              <StatCard icon={Calendar}       label="Event Sign-ups"   color="#9D00FF" value={stats?.events?.total}     sparkline={stats?.events?.sparkline_7d}     testid="dashboard-events-card"    onClick={openDetails('events')} />
              <div className="col-span-2">
                <ReviewsCard data={stats?.reviews} onClick={openDetails('reviews')} />
              </div>
            </div>

            {/* Live controls (right column on desktop) — replaced the old remote mini-player.
                The global PlayerBar at the bottom handles same-tab playback now. */}
            <div className="col-span-12 lg:col-span-4">
              <LiveControlsCard />
              <div className="mt-4 bg-[#0D0D0D] border border-white/8 rounded-2xl p-5" data-testid="dashboard-cta-card">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#39FF14]" />
                  <h3 className="text-xs uppercase tracking-[0.3em] text-[#39FF14] font-['JetBrains_Mono'] font-bold">Pro tip</h3>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed font-['Outfit']">
                  Use the player bar at the bottom of the screen for music. It stays with you across
                  every page — Home, Playlists, Menu, anywhere.
                </p>
              </div>
            </div>
          </div>
        )}

        <AnimatePresence>
          {detailsSource && (
            <CustomerDetailsModal
              source={detailsSource}
              onClose={() => setDetailsSource(null)}
            />
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
};

export default ClientDashboardPage;
